import { Component, signal, computed, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserHelper } from '@app/core/helpers/user.helper';
import { AuthenticationService } from '@app/core/services/authentication/authentication.service';
import { UserService } from '@app/core/services/user.service';
import { ConfirmationService } from 'primeng/api';
import { NavService } from '@app/core/services/nav.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ServerSideFileHelper } from '@app/core/helpers/server-side-file.helper';
import { PermissionService } from '@app/core/services/permission.service';
import { PermissionEnum } from '@app/generated/graphql';
import { IAlertTestItem } from '@app/account/notifications/interfaces/notifications-data.interfaces';

@Component({
  selector: 'app-profile-menu',
  templateUrl: './profile-menu.component.html',
  standalone: false,
})
export class ProfileMenuComponent {
  PermissionEnum = PermissionEnum;
  profilePopoverOpen: boolean = false;

  /** Alerts passed from global-layout – pass-through to app-alerts-menu. */
  alerts = input<IAlertTestItem[]>([]);
  /** Notifications passed from global-layout – pass-through to app-notifications-v2 (e.g. in profile menu on mobile). */
  notifications = input<IAlertTestItem[]>([]);

  // output signals – alerts (from app-alerts-menu)
  markAllAsRead = output<void>();
  archiveAllRead = output<void>();
  archiveAll = output<void>();
  toggleMarkAsRead = output<IAlertTestItem>();
  archiveAlert = output<IAlertTestItem>();
  alertClick = output<IAlertTestItem>();

  // output signals – notifications (from app-notifications-v2)
  notificationMarkAllAsRead = output<void>();
  notificationArchiveAllRead = output<void>();
  notificationArchiveAll = output<void>();
  notificationToggleMarkAsRead = output<IAlertTestItem>();
  notificationArchive = output<IAlertTestItem>();
  notificationClick = output<IAlertTestItem>();

  user = toSignal(this.userService.user$, {
    initialValue: this.userService.getUserValue(),
  });
  company = toSignal(this.userService.company$, {
    initialValue: this.userService.getCompanyValue(),
  });
  userFeedUnseenItemCount = signal<number>(0);
  alertsCount = signal<number>(0);
  notificationCount = signal<number>(0);

  showActionItemBadge = computed(
    () =>
      this.userFeedUnseenItemCount() > 0 ||
      this.alertsCount() > 0 ||
      this.notificationCount() > 0,
  );

  userFullName = computed(() =>
    this.user() ? UserHelper.getFullName(this.user()) : '',
  );
  userAvatarUrl = computed(() =>
    this.user()?.avatarFile
      ? ServerSideFileHelper.fullUrl(this.user()?.avatarFile)
      : null,
  );
  hasUserAvatar = computed(() => !!this.user()?.avatarFile?.url);

  constructor(
    public userService: UserService,
    private confirmationService: ConfirmationService,
    private authenticationService: AuthenticationService,
    public navService: NavService,
    public layoutService: LayoutService,
    public permissionService: PermissionService,
  ) {}

  onProfilePopoverShow(): void {
    this.profilePopoverOpen = true;
  }

  onProfilePopoverHide(): void {
    this.profilePopoverOpen = false;
  }

  onLogout(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as HTMLElement,
      header: 'Logout?',
      closable: true,
      closeOnEscape: true,
      message: 'Are you sure you want to logout of the AgrigateOne platform?',
      acceptButtonProps: {
        label: 'Logout',
        severity: 'danger',
      },
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'contrast',
        variant: 'text',
      },
      accept: () => this.authenticationService.logout(),
    });
  }

  onUserFeedUnseenItemCountChange(count: number): void {
    this.userFeedUnseenItemCount.set(count || 0);
  }

  onAlertsCountChange(count: number): void {
    this.alertsCount.set(count || 0);
  }

  onNotificationCountChange(count: number): void {
    this.notificationCount.set(count || 0);
  }
}
